// src/tabs/PostagemTab.js
import React, { useState, useMemo, useEffect } from "react";
import { 
  FileText, Truck, Printer, CheckCircle, Search, Mail, Loader2, AlertCircle, 
  ExternalLink, RefreshCw, Copy, Check, Info, ArrowRight, ShieldAlert, Award, Trash2, Plus
} from "lucide-react";
import { useProductionContext } from "../contexts/ProductionContext";
import { useOrderProcessing } from "../hooks/useOrderProcessing";
import { fiscalService } from "../services/fiscalService";
import { correiosService } from "../services/correiosService";
import { ordersService } from "../services/ordersService";
import { reverseLogisticsService } from "../services/reverseLogisticsService";
import { generateCertificatePDF } from "../utils/certificateGenerator";
import InvoiceGenerationModal from "../components/modals/InvoiceGenerationModal";
import RequestReverseLogisticsModal from "../components/correios/RequestReverseLogisticsModal";
import MonitoramentoSubTab from "../components/production/MonitoramentoSubTab";
import { pb } from "../services/pocketbase";
import { postageLabelsService } from "../services/postageLabelsService";
import { PDFDocument } from "pdf-lib";

// Auxiliar para carregar imagens assincronamente e converter para base64
const loadImgAsBase64 = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

// Auxiliar para recortar a etiqueta A4 original para o padrão de termica 10x15cm (utilizando as mesmas coordenadas homologadas)
const cropEtiquetaPdfBytes = async (base64) => {
  const CM_TO_PT = 28.3465;
  const pdfBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const srcDoc = await PDFDocument.load(pdfBytes);
  const [srcPage] = srcDoc.getPages();
  const { width, height } = srcPage.getSize();

  const cropBox = {
    left: 10,
    bottom: height - 428,
    right: 298,
    top: height - 8
  };

  const outDoc = await PDFDocument.create();
  const embedded = await outDoc.embedPage(srcPage, cropBox);

  const larguraPt = 10 * CM_TO_PT;
  const alturaPt = 15 * CM_TO_PT;
  const page = outDoc.addPage([larguraPt, alturaPt]);

  page.drawPage(embedded, {
    x: 0,
    y: 0,
    width: larguraPt,
    height: alturaPt,
  });

  return await outDoc.save();
};

// Auxiliar para gerar o PDF da DANFE Simplificada idêntico ao leiaute e estilos do HTML original
const generateDanfeSimplificadaPDF = async (groupOrOrder) => {
  const invoices = groupOrOrder.items?.[0]?.expand?.fiscal_invoices_via_order || [];
  const saidaInv = [...invoices].filter(i => i.tipo_nota === "garantia_saida" && i.nfe_status === "autorizada").sort((a, b) => new Date(b.created) - new Date(a.created))[0];
  const comedyInv = [...invoices].filter(i => i.tipo_nota === "venda" && i.nfe_status === "autorizada").sort((a, b) => new Date(b.created) - new Date(a.created))[0];
  const entradaInv = [...invoices].filter(i => i.tipo_nota === "garantia_entrada" && i.nfe_status === "autorizada").sort((a, b) => new Date(b.created) - new Date(a.created))[0];
  
  const invoice = saidaInv || comedyInv || entradaInv || groupOrOrder.activeInvoice || [...invoices].sort((a, b) => new Date(b.created) - new Date(a.created))[0];
  if (!invoice) return null;

  const isEntrada = invoice.tipo_nota === "garantia_entrada" || 
                    invoice.tipo_nota === "devolucao" || 
                    invoice.focus_payload?.tipo_documento === 0 || 
                    invoice.full_data?.focus_payload?.tipo_documento === 0 || 
                    false;

  try {
    const cnpjEmissor = invoice.cnpj_emissor || invoice.full_data?.cnpj_emissor || "";
    const configs = await pb.collection("fiscal_configs").getFullList();
    const config = configs.find(
      (c) => c.cnpj.replace(/\D/g, "") === cnpjEmissor.replace(/\D/g, "")
    ) || configs[0] || {};
    
    const firstItem = groupOrOrder.items?.[0] || {};
    const shippingAddress = firstItem.shipping?.modified_address || firstItem.shipping?.address || {};
    const customer = firstItem.order?.customer || {};

    const formatKey = (k) => {
      return k ? k.replace(/(\d{4})/g, "$1 ").trim() : "";
    };

    const doc = new window.jspdf.jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [100, 150]
    });

    const margin = 4;
    const w = 100 - margin * 2;
    const h = 150 - margin * 2;

    // Borda externa
    doc.rect(margin, margin, w, h);

    // Título
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`DANFE SIMPLIFICADO - ${isEntrada ? "ENTRADA" : "SAÍDA"}`, 50, margin + 6, { align: "center" });
    doc.line(margin, margin + 8, margin + w, margin + 8);

    // Logo / Emissor
    doc.setFontSize(8);
    doc.text("SEMPRE\nJOIAS", margin + 2, margin + 13);
    doc.line(margin + 20, margin + 8, margin + 20, margin + 25);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    const emitterText = [
      config.razao_social || "SEMPRE JOIAS LTDA",
      `CNPJ: ${config.cnpj || "46.223.109/0001-07"}`,
      config.endereco || "AV WALDEMAR CARLOS PEREIRA, 179 - SALA 6",
      `${config.bairro || "VILA MATILDE"} - ${config.cidade || "SAO PAULO"} - ${config.uf || "SP"} - CEP: ${config.cep || "03533-001"}`
    ];
    doc.text(emitterText, margin + 22, margin + 12);
    doc.line(margin, margin + 25, margin + w, margin + 25);

    // Código de Barras
    try {
      const barcodeUrl = `https://bwipjs-api.metafloor.com/?bcid=code128&text=${invoice.nfe_chave}&scale=3`;
      const base64Img = await loadImgAsBase64(barcodeUrl);
      if (base64Img) {
        doc.addImage(base64Img, "PNG", margin + 6, margin + 27, w - 12, 12);
      }
    } catch (e) {
      console.error("Erro ao desenhar código de barras no PDF:", e);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(formatKey(invoice.nfe_chave), 50, margin + 43, { align: "center" });
    doc.line(margin, margin + 45, margin + w, margin + 45);

    // Dados da NF
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    const emiDate = new Date(invoice.nfe_data_emissao || invoice.created).toLocaleDateString("pt-BR");
    doc.text(`Nº: ${invoice.nfe_numero}`, margin + 2, margin + 49);
    doc.text(`SÉRIE: ${invoice.nfe_serie}`, 50, margin + 49, { align: "center" });
    doc.text(`EMISSÃO: ${emiDate}`, margin + w - 2, margin + 49, { align: "right" });
    doc.line(margin, margin + 51, margin + w, margin + 51);

    // Título Destinatário
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, margin + 51, w, 6, "F");
    doc.rect(margin, margin + 51, w, 6, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(isEntrada ? "REMETENTE" : "DESTINATÁRIO", margin + 2, margin + 55);

    // Nome
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const destName = shippingAddress.recipient || customer.name || groupOrOrder.customerName;
    doc.setFont("helvetica", "bold");
    doc.text(destName, margin + 2, margin + 62);

    // Endereço
    doc.setFont("helvetica", "normal");
    const destAddress = [
      `${shippingAddress.street || ""}, ${shippingAddress.number || "SN"} ${shippingAddress.complemento ? '- ' + shippingAddress.complemento : ''}`,
      `${shippingAddress.district || ""} - ${shippingAddress.city || ""}/${(shippingAddress.statecode || shippingAddress.state || "").toUpperCase()}`,
      `CEP: ${shippingAddress.zip || ""}`
    ];
    doc.text(destAddress, margin + 2, margin + 66);

    return doc;
  } catch (err) {
    console.error("Erro ao gerar PDF da DANFE Simplificada:", err);
    return null;
  }
};

export default function PostagemTab({ findCatalogItem }) {
  const { orders: rawData, setOrders: setRawData } = useProductionContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [emittingOrderGroup, setEmittingOrderGroup] = useState(null);
  const [copiedTracking, setCopiedTracking] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // "all", "nfe", "label", "cert"

  // Controla o status de loading de cada pedido individualmente
  const [orderLoadings, setOrderLoadings] = useState({});
  const [fiscalConfigs, setFiscalConfigs] = useState([]);
  const [freeShippingOrder, setFreeShippingOrder] = useState(null);

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [batchLoading, setBatchLoading] = useState("");

  // Limpa a seleção ao alternar filtros ou buscar
  useEffect(() => {
    setSelectedOrders([]);
  }, [activeFilter, searchTerm]);
  const [confirmLabelGroup, setConfirmLabelGroup] = useState(null);
  const [confirmAddress, setConfirmAddress] = useState({
    recipient: "",
    street: "",
    number: "",
    complemento: "",
    district: "",
    city: "",
    state: "",
    zip: ""
  });
  const [confirmServiceCode, setConfirmServiceCode] = useState("03298");
  const [confirmAr, setConfirmAr] = useState(false);
  const [confirmSeguro, setConfirmSeguro] = useState(false);
  const [confirmValorSegurado, setConfirmValorSegurado] = useState(0);

  // Sub-abas e Logística Reversa
  const [activeSubTab, setActiveSubTab] = useState("postagem"); // "postagem" ou "reversa"
  const [activeQueueType, setActiveQueueType] = useState("venda"); // "venda" ou "repostagem"
  const [forcedInvoiceTipoNota, setForcedInvoiceTipoNota] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [selectedDetailsOrder, setSelectedDetailsOrder] = useState(null);
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [reverseRequests, setReverseRequests] = useState([]);
  const [loadingReversa, setLoadingReversa] = useState(false);
  const [isReversaModalOpen, setIsReversaModalOpen] = useState(false);
  const [activeReversaFilter, setActiveReversaFilter] = useState("all");

  const loadReverseRequests = async () => {
    setLoadingReversa(true);
    try {
      const data = await reverseLogisticsService.fetchRequests();
      setReverseRequests(data);
    } catch (err) {
      console.error("Erro ao carregar logísticas reversas:", err);
    } finally {
      setLoadingReversa(false);
    }
  };

  useEffect(() => {
    loadReverseRequests();
  }, []);

  const handleSyncReversa = async (req) => {
    try {
      await reverseLogisticsService.syncRequest(req);
      await loadReverseRequests();
      alert("Status de logística reversa atualizado!");
    } catch (err) {
      alert("Erro ao sincronizar status: " + err.message);
    }
  };

  const handleDeleteReversa = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta solicitação de logística reversa?")) {
      return;
    }
    try {
      await reverseLogisticsService.deleteRequest(id);
      await loadReverseRequests();
      alert("Solicitação excluída com sucesso.");
    } catch (err) {
      alert("Erro ao excluir solicitação: " + err.message);
    }
  };

  const handleRevalidateReversa = async (req) => {
    const days = window.prompt("Quantos dias deseja estender o prazo de postagem desta autorização?", "15");
    if (days === null) return;
    const validityDays = Number(days);
    if (isNaN(validityDays) || validityDays <= 0) {
      alert("Por favor, digite um número de dias válido.");
      return;
    }
    
    try {
      setOrderLoadings(prev => ({ ...prev, [req.id]: "Revalidando..." }));
      await reverseLogisticsService.revalidateRequest(req, validityDays);
      await loadReverseRequests();
      alert("Prazo de postagem estendido com sucesso!");
    } catch (err) {
      alert("Erro ao revalidar autorização: " + err.message);
    } finally {
      setOrderLoadings(prev => {
        const copy = { ...prev };
        delete copy[req.id];
        return copy;
      });
    }
  };

  const reverseStats = useMemo(() => {
    const total = reverseRequests.length;
    const pending = reverseRequests.filter(r => r.status === "GERADO").length;
    const inTransit = reverseRequests.filter(r => r.status === "POSTADO").length;
    const delivered = reverseRequests.filter(r => r.status === "ENTREGUE").length;
    return { total, pending, inTransit, delivered };
  }, [reverseRequests]);

  const filteredReversaRequests = useMemo(() => {
    return reverseRequests.filter(req => {
      if (activeReversaFilter !== "all" && req.status !== activeReversaFilter) {
        return false;
      }
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const numMatch = String(req.order_number).toLowerCase().includes(term);
        const nameMatch = String(req.customer_name).toLowerCase().includes(term);
        const codeMatch = String(req.codigo_autorizacao).toLowerCase().includes(term);
        return numMatch || nameMatch || codeMatch;
      }
      return true;
    });
  }, [reverseRequests, activeReversaFilter, searchTerm]);

  useEffect(() => {
    fiscalService.fetchConfigs()
      .then(setFiscalConfigs)
      .catch(err => console.error("Erro ao carregar configurações fiscais:", err));
  }, []);

  // Recupera apenas os pedidos que estão no status logístico "AGUARDANDO POSTAGEM"
  const groupedOrders = useOrderProcessing(
    rawData,
    searchTerm,
    "AGUARDANDO POSTAGEM",
    findCatalogItem
  );

  // Mapeia, enriquece e FILTRA os pedidos (excluindo Retirada e Motoboy)
  const processedOrders = useMemo(() => {
    return groupedOrders
      .filter((group) => {
        // EXCLUI pedidos que sejam retirada ou motoboy
        const isRetiradaOuMotoboy = /retirada|motoboy/i.test(group.shippingMethod || "");
        return !isRetiradaOuMotoboy;
      })
      .map((group) => {
        const firstItem = group.items?.[0] || {};
        const invoices = firstItem.expand?.fiscal_invoices_via_order || [];
        const sortedInvoices = [...invoices].sort((a, b) => new Date(b.created) - new Date(a.created));
        const isRepostagemCheck = String(group.orderNumber || "").toLowerCase().includes("repostagem");
        const authSaidaInvoice = isRepostagemCheck
          ? sortedInvoices.find(inv => inv.tipo_nota === "garantia_saida" && inv.nfe_status === "autorizada")
          : (sortedInvoices.find(inv => (inv.tipo_nota === "garantia_saida" || inv.tipo_nota === "venda") && inv.nfe_status === "autorizada") ||
             sortedInvoices.find(inv => inv.nfe_status === "autorizada"));
        const activeInvoice = authSaidaInvoice || (sortedInvoices.length > 0 ? sortedInvoices[0] : null);
        
        const nfeStatus = activeInvoice ? activeInvoice.nfe_status : "pendente";
        const hasCorreiosLabel = !!firstItem.full_data?.correios_prepostagem_id;
        const trackingCode = firstItem.full_data?.correios_codigo_objeto || "";
        const isCertificatePrinted = group.items.every(item => item.certificatePrinted === true);

        const isRepostagem = String(group.orderNumber || "").toLowerCase().includes("repostagem");
        const hasEntrada = invoices.some(inv => inv.tipo_nota === "garantia_entrada" && inv.nfe_status === "autorizada");
        const hasSaida = isRepostagem
          ? invoices.some(inv => inv.tipo_nota === "garantia_saida" && inv.nfe_status === "autorizada")
          : (invoices.some(inv => inv.tipo_nota === "garantia_saida" && inv.nfe_status === "autorizada") || invoices.some(inv => inv.tipo_nota === "venda" && inv.nfe_status === "autorizada"));

        // Sub-status de validação
        const nfeOk = isRepostagem ? hasSaida : (nfeStatus === "autorizada");
        const labelOk = hasCorreiosLabel;
        const certOk = isCertificatePrinted;
        const canFinalize = nfeOk && labelOk && certOk;

        return {
          ...group,
          activeInvoice,
          nfeStatus,
          hasCorreiosLabel,
          trackingCode,
          isCertificatePrinted,
          isRepostagem,
          hasEntrada,
          hasSaida,
          nfeOk,
          labelOk,
          certOk,
          canFinalize
        };
      });
  }, [groupedOrders]);

  // Filtra por tipo de fila (venda normal ou repostagem)
  const filteredByTypeOrders = useMemo(() => {
    return processedOrders.filter((o) => {
      const isRep = String(o.orderNumber || "").toLowerCase().includes("repostagem");
      return activeQueueType === "repostagem" ? isRep : !isRep;
    });
  }, [processedOrders, activeQueueType]);

  // Contadores para os Cards de Resumo
  const stats = useMemo(() => {
    let total = filteredByTypeOrders.length;
    let pendingNfe = 0;
    let pendingLabel = 0;
    let pendingCert = 0;

    filteredByTypeOrders.forEach((o) => {
      if (!o.nfeOk) pendingNfe++;
      if (!o.labelOk) pendingLabel++;
      if (!o.certOk) pendingCert++;
    });

    return { total, pendingNfe, pendingLabel, pendingCert };
  }, [filteredByTypeOrders]);

  // Aplica o filtro selecionado nos cards do topo
  const displayOrders = useMemo(() => {
    if (activeFilter === "nfe") {
      return filteredByTypeOrders.filter(o => !o.nfeOk);
    }
    if (activeFilter === "label") {
      return filteredByTypeOrders.filter(o => !o.labelOk);
    }
    if (activeFilter === "cert") {
      return filteredByTypeOrders.filter(o => !o.certOk);
    }
    return filteredByTypeOrders;
  }, [filteredByTypeOrders, activeFilter]);

  const toggleFilter = (filterType) => {
    setActiveFilter(prev => prev === filterType ? "all" : filterType);
  };

  const handleSelectMotoboy = async (group) => {
    try {
      setOrderLoadings(prev => ({ ...prev, [group.orderNumber]: "Movendo..." }));
      await ordersService.updateLogisticsStatusBatch(group.referenceIds, "MOTOBOY");
      alert("Pedido direcionado para o status MOTOBOY!");
    } catch (err) {
      alert("Erro ao mover para MOTOBOY: " + err.message);
    } finally {
      setOrderLoadings(prev => {
        const copy = { ...prev };
        delete copy[group.orderNumber];
        return copy;
      });
    }
  };

  const openConfirmLabelModal = (group) => {
    const firstItem = group.items?.[0] || {};
    const invoices = firstItem.expand?.fiscal_invoices_via_order || [];
    const sortedInvoices = [...invoices].sort((a, b) => new Date(b.created) - new Date(a.created));
    const isRepostagem = String(group.orderNumber || "").toLowerCase().includes("repostagem");
    const activeInvoice = isRepostagem
      ? sortedInvoices.find(inv => inv.tipo_nota === "garantia_saida" && inv.nfe_status === "autorizada")
      : (group.activeInvoice?.nfe_status === "autorizada" ? group.activeInvoice : sortedInvoices.find(inv => inv.nfe_status === "autorizada"));

    if (!activeInvoice?.nfe_chave) {
      const wantEmit = window.confirm(
        `O pedido #${group.orderNumber} ainda não possui Nota Fiscal (NF-e) de saída autorizada.\n\nDevido à regra do CONFAZ para contribuintes de ICMS, não é permitido gerar pré-postagem sem NF-e (via DC-e). Deseja emitir a NF-e agora?`
      );
      if (wantEmit) {
        if (isRepostagem) {
          const hasEntrada = invoices.some(inv => inv.tipo_nota === "garantia_entrada" && inv.nfe_status === "autorizada");
          setForcedInvoiceTipoNota(hasEntrada ? "garantia_saida" : "garantia_entrada");
        } else {
          setForcedInvoiceTipoNota(null);
        }
        setEditingInvoice(null);
        setEmittingOrderGroup(group);
      }
      return;
    }

    const shippingAddress = firstItem.shipping?.modified_address || firstItem.shipping?.address || {};
    const customer = firstItem.order?.customer || {};

    setConfirmAddress({
      recipient: shippingAddress.recipient || customer.name || group.customerName || "",
      street: shippingAddress.street || "",
      number: shippingAddress.number || "",
      complemento: shippingAddress.complemento || "",
      district: shippingAddress.district || "",
      city: shippingAddress.city || "",
      state: shippingAddress.statecode || shippingAddress.state || "",
      zip: shippingAddress.zip || ""
    });

    const shippingMethodUpper = String(group.shippingMethod || "").trim().toUpperCase();
    let serviceCode = "03298"; // PAC por padrão
    if (shippingMethodUpper.includes("SEDEX 10") || shippingMethodUpper.includes("SEDEX10") || shippingMethodUpper.includes("SEDEX-10") || shippingMethodUpper.includes("03158")) {
      serviceCode = "03158"; // SEDEX 10
    } else if (shippingMethodUpper.includes("SEDEX 12") || shippingMethodUpper.includes("SEDEX12") || shippingMethodUpper.includes("SEDEX-12") || shippingMethodUpper.includes("03140")) {
      serviceCode = "03140"; // SEDEX 12
    } else if (shippingMethodUpper.includes("SEDEX") || shippingMethodUpper.includes("03220")) {
      serviceCode = "03220"; // SEDEX
    }
    setConfirmServiceCode(serviceCode);
    setConfirmAr(false);
    setConfirmSeguro(false);
    setConfirmValorSegurado(group.totalValue || 0);
    setConfirmLabelGroup(group);
  };

  const handleZipChange = async (val) => {
    setConfirmAddress(prev => ({ ...prev, zip: val }));
    const cleanCep = val.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        if (res.ok) {
          const data = await res.json();
          if (!data.erro) {
            setConfirmAddress(prev => ({
              ...prev,
              street: data.logradouro || prev.street || "",
              district: data.bairro || prev.district || "",
              city: data.localidade || prev.city || "",
              state: data.uf || prev.state || "",
              statecode: data.uf || prev.statecode || "",
            }));
          }
        }
      } catch (err) {
        console.error("Erro ao buscar CEP:", err);
      }
    }
  };

  const handleGenerateCorreiosLabel = (group) => {
    const firstItem = group.items?.[0] || {};
    const hasExistingLabel = !!firstItem.full_data?.correios_prepostagem_id;

    if (hasExistingLabel) {
      // Se já possui uma etiqueta gerada, reabre diretamente
      generateLabelWithService(group, null);
      return;
    }

    openConfirmLabelModal(group);
  };

  const generateLabelWithService = async (group, chosenServiceCode, customAddress = null, ar = false, seguro = false, valorSegurado = 0) => {
    const orderNum = group.orderNumber;
    const firstItem = group.items?.[0] || {};
    const hasExistingLabel = !!firstItem.full_data?.correios_prepostagem_id;
    const existingPrepostId = firstItem.full_data?.correios_prepostagem_id;

    const setOrderLoading = (status) => {
      setOrderLoadings(prev => ({ ...prev, [orderNum]: status }));
    };

    if (hasExistingLabel) {
      setOrderLoading("Buscando etiqueta salva...");
      try {
        const shipment = await postageLabelsService.getShipment(orderNum);
        if (shipment && shipment.etiqueta_pdf) {
          const fileUrl = pb.files.getUrl(shipment, shipment.etiqueta_pdf, { token: pb.authStore.token });
          window.open(fileUrl, "_blank");
          setOrderLoadings(prev => {
            const copy = { ...prev };
            delete copy[orderNum];
            return copy;
          });
          return;
        }
      } catch (err) {
        console.warn("Falha ao buscar etiqueta salva no cache, caindo de volta para a API dos Correios:", err);
      }
    }

    setOrderLoading(hasExistingLabel ? "Reimprimindo..." : "Autenticando...");

    try {
      let prepostagemId = existingPrepostId;
      let codigoObjeto = firstItem.full_data?.correios_codigo_objeto || "";

      if (!hasExistingLabel) {
        // Fluxo de criação de nova pré-postagem
        const shippingAddress = customAddress || firstItem.shipping?.modified_address || firstItem.shipping?.address || {};
        const customer = firstItem.order?.customer || {};

        // Limpa e trata CEP
        const cleanCep = (shippingAddress.zip || "").replace(/\D/g, "");
        if (cleanCep.length !== 8) {
          throw new Error("CEP do destinatário inválido ou incompleto.");
        }

        // Limpa e separa DDD do Celular
        const rawPhone = (customer.phone || "").replace(/\D/g, "");
        let ddd = "11";
        let phoneNum = "";
        if (rawPhone.length >= 10) {
          ddd = rawPhone.substring(0, 2);
          phoneNum = rawPhone.substring(2);
        } else {
          phoneNum = rawPhone || "999999999";
        }

        // Limpa CPF/CNPJ
        const cleanCpf = (customer.cpf || "").replace(/\D/g, "");

        setOrderLoading("Criando pré-postagem...");

        let codigoServico = chosenServiceCode;
        if (!codigoServico) {
          codigoServico = "03298"; // PAC com contrato padrão
          const shippingMethodUpper = String(group.shippingMethod || "").toUpperCase();
          if (shippingMethodUpper.includes("SEDEX 10") || shippingMethodUpper.includes("SEDEX10") || shippingMethodUpper.includes("SEDEX-10") || shippingMethodUpper.includes("03158")) {
            codigoServico = "03158"; // SEDEX 10
          } else if (shippingMethodUpper.includes("SEDEX 12") || shippingMethodUpper.includes("SEDEX12") || shippingMethodUpper.includes("SEDEX-12") || shippingMethodUpper.includes("03140")) {
            codigoServico = "03140"; // SEDEX 12
          } else if (shippingMethodUpper.includes("SEDEX") || shippingMethodUpper.includes("03220")) {
            codigoServico = "03220"; // SEDEX com contrato
          } else if (shippingMethodUpper.includes("PAC") || shippingMethodUpper.includes("03298")) {
            codigoServico = "03298"; // PAC com contrato
          }
        }

        // Dados do Pedido Formatados para os Correios
        const dadosPedido = {
          codigoServico,
          chaveNFe: group.activeInvoice?.nfe_chave || "",
          numeroNotaFiscal: group.activeInvoice?.nfe_numero || "",
          destinatario: {
            nome: shippingAddress.recipient || customer.name || group.customerName,
            cpfCnpj: cleanCpf,
            dddCelular: ddd,
            celular: phoneNum,
            email: customer.email || "cliente@email.com",
            endereco: {
              cep: cleanCep,
              logradouro: shippingAddress.street || "",
              numero: shippingAddress.number || "SN",
              complemento: shippingAddress.complemento || "",
              bairro: shippingAddress.district || "",
              cidade: shippingAddress.city || "",
              uf: (shippingAddress.statecode || shippingAddress.state || "").toUpperCase()
            }
          },
          valorDeclaracao: group.totalValue.toFixed(2),
          conteudoDeclaracao: group.items.map(i => i.sku).join(", ").substring(0, 40) || "Joia em prata 925",
          ar,
          seguro,
          valorSegurado
        };

        const result = await correiosService.criarPrePostagem(dadosPedido);
        prepostagemId = result.id;
        codigoObjeto = result.codigoObjeto;
      }

      // Solicita a etiqueta
      setOrderLoading("Gerando rótulo...");
      const idRecibo = await correiosService.gerarRotulo(prepostagemId);

      // Polling para baixar o arquivo
      setOrderLoading("Processando rótulo...");
      const base64Pdf = await correiosService.baixarRotulo(idRecibo);

      // Abre o PDF apenas se for reimpressão (já existia etiqueta)
      if (hasExistingLabel) {
        setOrderLoading("Abrindo etiqueta...");
        await correiosService.abrirEtiquetaPDF(base64Pdf);
      }

      // Cacheia no postage_labels
      try {
        const croppedBytes = await cropEtiquetaPdfBytes(base64Pdf);
        const labelBlob = new Blob([croppedBytes], { type: "application/pdf" });
        await postageLabelsService.saveShipment(orderNum, codigoObjeto, prepostagemId);
        await postageLabelsService.uploadPdf(orderNum, "etiqueta_pdf", labelBlob, `etiqueta_${orderNum}.pdf`);
      } catch (err) {
        console.warn("Falha ao salvar etiqueta no cache postage_labels:", err);
      }

      // Se for a primeira geração, salva no PocketBase
      if (!hasExistingLabel) {
        await ordersService.saveCorreiosDetails(group.items, prepostagemId, codigoObjeto, customAddress);
        
        // Atualiza o estado do React imediatamente para atualizar a linha
        setRawData((prev) => 
          prev.map((item) => {
            if (group.items.some(i => i.id === item.id)) {
              const fullData = { ...item.full_data };
              fullData.correios_prepostagem_id = prepostagemId;
              fullData.correios_codigo_objeto = codigoObjeto;
              fullData.correios_etiqueta_gerada_em = new Date().toISOString();
              if (!fullData.shipping) fullData.shipping = {};
              fullData.shipping.tracking = codigoObjeto;
              if (customAddress) {
                fullData.shipping.modified_address = {
                  ...customAddress
                };
              }
              return { ...item, full_data: fullData };
            }
            return item;
          })
        );
      }

      alert(hasExistingLabel ? "Etiqueta reaberta com sucesso!" : "Etiqueta gerada e vinculada com sucesso!");
    } catch (err) {
      console.error(err);
      alert(`Falha no processo dos Correios: ${err.message}`);
    } finally {
      setOrderLoading("");
    }
  };

  // Gera uma nova etiqueta limpando os detalhes anteriores (Repostagem)
  const handleRepostLabel = async (group) => {
    if (!window.confirm(`Deseja gerar uma nova etiqueta para o pedido #${group.orderNumber}? O código de rastreamento atual (${group.trackingCode}) será descartado.`)) {
      return;
    }
    
    setOrderLoadings(prev => ({ ...prev, [group.orderNumber]: "Limpando..." }));
    try {
      await ordersService.clearCorreiosDetails(group.items);
      
      // Atualiza o estado do React imediatamente para resetar a linha
      setRawData((prev) => 
        prev.map((item) => {
          if (group.items.some(i => i.id === item.id)) {
            const fullData = { ...item.full_data };
            delete fullData.correios_prepostagem_id;
            delete fullData.correios_codigo_objeto;
            delete fullData.correios_etiqueta_gerada_em;
            if (fullData.shipping) {
              delete fullData.shipping.tracking;
            }
            return { ...item, full_data: fullData };
          }
          return item;
        })
      );
      
      // Monta um grupo atualizado temporário sem etiqueta
      const updatedGroup = {
        ...group,
        hasCorreiosLabel: false,
        trackingCode: "",
        items: group.items.map(item => {
          const fullData = { ...item.full_data };
          delete fullData.correios_prepostagem_id;
          delete fullData.correios_codigo_objeto;
          delete fullData.correios_etiqueta_gerada_em;
          if (fullData.shipping) {
            delete fullData.shipping.tracking;
          }
          return { ...item, full_data: fullData };
        })
      };

      // Dispara o modal de confirmação para a nova etiqueta
      openConfirmLabelModal(updatedGroup);
    } catch (err) {
      console.error(err);
      alert(`Falha ao resetar detalhes de postagem: ${err.message}`);
    } finally {
      setOrderLoadings(prev => ({ ...prev, [group.orderNumber]: "" }));
    }
  };

  // Imprime os certificados e atualiza no banco
  const handlePrintCertificates = async (group) => {
    try {
      const success = generateCertificatePDF(group.items, findCatalogItem);
      if (success) {
        await ordersService.markCertificatesPrinted(group.items);
      }
    } catch (err) {
      alert("Erro ao gerar certificados: " + err.message);
    }
  };

  // Impressão Unificada (DANFE + Correios + Certificados) em um único PDF mesclado
  const handlePrintComboUnified = async (group) => {
    const orderNum = group.orderNumber;
    
    const setOrderLoading = (status) => {
      setOrderLoadings(prev => ({ ...prev, [orderNum]: status }));
    };

    setOrderLoading("Carregando PDFs...");
    try {
      // 1. Busca cache de postage_labels
      let shipment = await postageLabelsService.getShipment(orderNum);
      const pdfsToMerge = []; // Array de ArrayBuffers

      // --- ETAPA A: DANFE SIMPLIFICADA ---
      setOrderLoading("Buscando DANFE...");
      let danfeBlob = null;

      // Primeiro verifica se há no cache postage_labels
      if (shipment && shipment.danfe_pdf) {
        const fileUrl = pb.files.getUrl(shipment, shipment.danfe_pdf, { token: pb.authStore.token });
        const res = await fetch(fileUrl);
        if (res.ok) {
          danfeBlob = await res.blob();
        }
      }

      // Fallback: se não estiver no postage_labels, gera a DANFE Simplificada formatada em PDF usando jsPDF
      if (!danfeBlob) {
        const invoice = group.activeInvoice || (group.items?.[0]?.expand?.fiscal_invoices_via_order || []).sort((a, b) => new Date(b.created) - new Date(a.created))[0];
        if (invoice) {
          setOrderLoading("Gerando DANFE...");
          const pdfDoc = await generateDanfeSimplificadaPDF(group);
          if (pdfDoc) {
            const arrayBuf = pdfDoc.output("arraybuffer");
            danfeBlob = new Blob([arrayBuf], { type: "application/pdf" });
            try {
              await postageLabelsService.uploadPdf(orderNum, "danfe_pdf", danfeBlob, `danfe_${orderNum}.pdf`);
            } catch (err) {
              console.warn("Falha ao salvar DANFE no cache:", err);
            }
          }
        }
      }

      // Se achou, adiciona aos PDFs a serem mesclados
      if (danfeBlob) {
        const arrayBuf = await danfeBlob.arrayBuffer();
        pdfsToMerge.push(arrayBuf);
      } else {
        // Se a nota não foi emitida, avisa o usuário
        if (!group.activeInvoice) {
          const emit = window.confirm(`A Nota Fiscal do pedido #${orderNum} ainda não foi emitida. Deseja emitir agora ou continuar sem a DANFE?`);
          if (emit) {
            setEmittingOrderGroup(group);
            return;
          }
        } else {
          console.log("DANFE não disponível em PDF. Continuando sem ela...");
        }
      }

      // --- ETAPA B: ETIQUETA CORREIOS ---
      setOrderLoading("Buscando Etiqueta...");
      let labelBlob = null;

      // Primeiro verifica se há no cache postage_labels (e se o rastreio bate com o atual)
      if (shipment && shipment.etiqueta_pdf && group.hasCorreiosLabel && (!shipment.tracking_code || shipment.tracking_code === group.trackingCode)) {
        const fileUrl = pb.files.getUrl(shipment, shipment.etiqueta_pdf, { token: pb.authStore.token });
        const res = await fetch(fileUrl);
        if (res.ok) {
          labelBlob = await res.blob();
        }
      }

      // Fallback: se não estiver cacheada, mas temos o ID de pré-postagem, tentamos baixar da API
      const firstItem = group.items?.[0] || {};
      const prepostagemId = firstItem.full_data?.correios_prepostagem_id;
      
      if (!labelBlob && prepostagemId) {
        setOrderLoading("Baixando e recortando etiqueta...");
        const idRecibo = await correiosService.gerarRotulo(prepostagemId);
        const base64 = await correiosService.baixarRotulo(idRecibo);
        
        // Recorta a etiqueta para 10x15cm
        const croppedBytes = await cropEtiquetaPdfBytes(base64);
        labelBlob = new Blob([croppedBytes], { type: "application/pdf" });

        // Salva no banco de dados para evitar re-geração
        try {
          await postageLabelsService.saveShipment(orderNum, firstItem.full_data?.correios_codigo_objeto || "", prepostagemId);
          await postageLabelsService.uploadPdf(orderNum, "etiqueta_pdf", labelBlob, `etiqueta_${orderNum}.pdf`);
        } catch (err) {
          console.warn("Falha ao salvar etiqueta no cache:", err);
        }
      }

      if (labelBlob) {
        const arrayBuf = await labelBlob.arrayBuffer();
        pdfsToMerge.push(arrayBuf);
      } else {
        const genLabel = window.confirm(`A etiqueta de envio dos Correios para o pedido #${orderNum} ainda não foi gerada. Deseja gerar agora?`);
        if (genLabel) {
          openConfirmLabelModal(group);
          return;
        }
      }

      // --- ETAPA C: CERTIFICADOS ---
      setOrderLoading("Buscando Certificados...");
      let certBlob = null;

      if (shipment && shipment.certificados_pdf) {
        const fileUrl = pb.files.getUrl(shipment, shipment.certificados_pdf, { token: pb.authStore.token });
        const res = await fetch(fileUrl);
        if (res.ok) {
          certBlob = await res.blob();
        }
      }

      // Fallback: se não estiver no cache, gera dinamicamente no frontend
      if (!certBlob) {
        setOrderLoading("Gerando certificados...");
        // passamos returnDoc = true para obter o doc do jsPDF
        const pdfDoc = generateCertificatePDF(group.items, findCatalogItem, true);
        if (pdfDoc) {
          // Obtém o ArrayBuffer direto do jsPDF
          const arrayBuf = pdfDoc.output("arraybuffer");
          certBlob = new Blob([arrayBuf], { type: "application/pdf" });
          
          // Marca no banco de dados como impresso
          await ordersService.markCertificatesPrinted(group.items);

          // Salva no cache do postage_labels
          try {
            await postageLabelsService.uploadPdf(orderNum, "certificados_pdf", certBlob, `certificados_${orderNum}.pdf`);
          } catch (err) {
            console.warn("Falha ao salvar certificados no cache:", err);
          }
        }
      }

      if (certBlob) {
        const arrayBuf = await certBlob.arrayBuffer();
        pdfsToMerge.push(arrayBuf);
      }

      // --- ETAPA D: MESCLAR E EXIBIR ---
      if (pdfsToMerge.length === 0) {
        throw new Error("Nenhum PDF disponível para impressão.");
      }

      setOrderLoading("Mesclando PDFs...");
      const mergedPdfDoc = await PDFDocument.create();
      for (const pdfBytes of pdfsToMerge) {
        try {
          const doc = await PDFDocument.load(pdfBytes);
          const copiedPages = await mergedPdfDoc.copyPages(doc, doc.getPageIndices());
          copiedPages.forEach((page) => mergedPdfDoc.addPage(page));
        } catch (loadErr) {
          console.error("Erro ao carregar PDF individual para mesclagem:", loadErr);
        }
      }

      const mergedPdfBytes = await mergedPdfDoc.save();
      const mergedBlob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const mergedUrl = URL.createObjectURL(mergedBlob);

      setOrderLoading("Imprimindo...");
      window.open(mergedUrl, "_blank");
    } catch (err) {
      console.error(err);
      alert(`Falha ao imprimir combo do pedido #${orderNum}: ${err.message}`);
    } finally {
      setOrderLoading("");
    }
  };

  // Finaliza a postagem e remove o pedido deste fluxo
  const handleFinalizePostage = async (group) => {
    if (!window.confirm(`Deseja marcar o pedido #${group.orderNumber} como pronto para postar? Ele sairá da Expedição.`)) {
      return;
    }
    try {
      await ordersService.updateLogisticsStatusBatch(group.referenceIds, "PRONTO PARA POSTAR");
      alert("Pedido pronto para postar! Enviado com sucesso.");
    } catch (err) {
      alert("Erro ao atualizar status do pedido: " + err.message);
    }
  };

  // Impressão de combos em lote para todos os pedidos selecionados
  const handlePrintComboBatch = async () => {
    const selectedList = displayOrders.filter(o => selectedOrders.includes(o.orderNumber));
    if (selectedList.length === 0) return;

    setBatchLoading("print");
    const pdfsToMerge = [];
    let skippedCount = 0;

    try {
      for (const group of selectedList) {
        const orderNum = group.orderNumber;
        let shipment = await postageLabelsService.getShipment(orderNum);

        // --- ETAPA A: DANFE ---
        let danfeBlob = null;
        if (shipment && shipment.danfe_pdf) {
          const fileUrl = pb.files.getUrl(shipment, shipment.danfe_pdf, { token: pb.authStore.token });
          const res = await fetch(fileUrl);
          if (res.ok) danfeBlob = await res.blob();
        }

        if (!danfeBlob) {
          const invoice = group.activeInvoice || (group.items?.[0]?.expand?.fiscal_invoices_via_order || []).sort((a, b) => new Date(b.created) - new Date(a.created))[0];
          if (invoice) {
            const pdfDoc = await generateDanfeSimplificadaPDF(group);
            if (pdfDoc) {
              const arrayBuf = pdfDoc.output("arraybuffer");
              danfeBlob = new Blob([arrayBuf], { type: "application/pdf" });
              try {
                await postageLabelsService.uploadPdf(orderNum, "danfe_pdf", danfeBlob, `danfe_${orderNum}.pdf`);
              } catch (e) {}
            }
          }
        }

        if (danfeBlob) {
          const arrayBuf = await danfeBlob.arrayBuffer();
          pdfsToMerge.push(arrayBuf);
        }

        // --- ETAPA B: ETIQUETA CORREIOS ---
        let labelBlob = null;
        if (shipment && shipment.etiqueta_pdf && group.hasCorreiosLabel && (!shipment.tracking_code || shipment.tracking_code === group.trackingCode)) {
          const fileUrl = pb.files.getUrl(shipment, shipment.etiqueta_pdf, { token: pb.authStore.token });
          const res = await fetch(fileUrl);
          if (res.ok) labelBlob = await res.blob();
        }

        const firstItem = group.items?.[0] || {};
        const prepostagemId = firstItem.full_data?.correios_prepostagem_id;
        
        if (!labelBlob && prepostagemId) {
          try {
            const idRecibo = await correiosService.gerarRotulo(prepostagemId);
            const base64 = await correiosService.baixarRotulo(idRecibo);
            
            // Recorta a etiqueta para 10x15cm
            const croppedBytes = await cropEtiquetaPdfBytes(base64);
            labelBlob = new Blob([croppedBytes], { type: "application/pdf" });
            
            await postageLabelsService.saveShipment(orderNum, firstItem.full_data?.correios_codigo_objeto || "", prepostagemId);
            await postageLabelsService.uploadPdf(orderNum, "etiqueta_pdf", labelBlob, `etiqueta_${orderNum}.pdf`);
          } catch (e) {
            console.warn(`Erro ao gerar etiqueta dos Correios para o pedido #${orderNum} no lote:`, e);
          }
        }

        if (labelBlob) {
          const arrayBuf = await labelBlob.arrayBuffer();
          pdfsToMerge.push(arrayBuf);
        }

        // --- ETAPA C: CERTIFICADOS ---
        let certBlob = null;
        if (shipment && shipment.certificados_pdf) {
          const fileUrl = pb.files.getUrl(shipment, shipment.certificados_pdf, { token: pb.authStore.token });
          const res = await fetch(fileUrl);
          if (res.ok) certBlob = await res.blob();
        }

        if (!certBlob) {
          try {
            const pdfDoc = generateCertificatePDF(group.items, findCatalogItem, true);
            if (pdfDoc) {
              const arrayBuf = pdfDoc.output("arraybuffer");
              certBlob = new Blob([arrayBuf], { type: "application/pdf" });
              await ordersService.markCertificatesPrinted(group.items);
              await postageLabelsService.uploadPdf(orderNum, "certificados_pdf", certBlob, `certificados_${orderNum}.pdf`);
            }
          } catch (e) {
            console.warn(`Erro ao gerar certificados para o pedido #${orderNum} no lote:`, e);
          }
        }

        if (certBlob) {
          const arrayBuf = await certBlob.arrayBuffer();
          pdfsToMerge.push(arrayBuf);
        }

        if (!danfeBlob && !labelBlob && !certBlob) {
          skippedCount++;
        }
      }

      if (pdfsToMerge.length === 0) {
        throw new Error("Nenhum PDF disponível para impressão no lote selecionado.");
      }

      const mergedPdfDoc = await PDFDocument.create();
      for (const pdfBytes of pdfsToMerge) {
        try {
          const doc = await PDFDocument.load(pdfBytes);
          const copiedPages = await mergedPdfDoc.copyPages(doc, doc.getPageIndices());
          copiedPages.forEach((page) => mergedPdfDoc.addPage(page));
        } catch (loadErr) {
          console.error("Erro ao carregar PDF individual no lote para mesclagem:", loadErr);
        }
      }

      const mergedPdfBytes = await mergedPdfDoc.save();
      const mergedBlob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const mergedUrl = URL.createObjectURL(mergedBlob);

      window.open(mergedUrl, "_blank");
      
      if (skippedCount > 0) {
        alert(`Fila em lote gerada com sucesso! ${skippedCount} pedido(s) foram pulados por não possuírem nenhum documento emitido.`);
      }
    } catch (err) {
      alert("Erro ao gerar impressão em lote: " + err.message);
    } finally {
      setBatchLoading("");
    }
  };

  // Finalização em lote de pedidos selecionados
  const handleFinalizePostageBatch = async () => {
    const selectedList = displayOrders.filter(o => selectedOrders.includes(o.orderNumber));
    if (selectedList.length === 0) return;

    if (!window.confirm(`Deseja marcar os ${selectedList.length} pedidos selecionados como prontos para postar? Eles sairão da Expedição.`)) {
      return;
    }

    setBatchLoading("finalize");
    try {
      const allReferenceIds = selectedList.flatMap(o => o.referenceIds);
      await ordersService.updateLogisticsStatusBatch(allReferenceIds, "PRONTO PARA POSTAR");
      alert(`${selectedList.length} pedidos foram marcados como prontos para postar!`);
      setSelectedOrders([]);
    } catch (err) {
      alert("Erro ao finalizar pedidos em lote: " + err.message);
    } finally {
      setBatchLoading("");
    }
  };

  // Consulta e sincroniza status de NF-e processando
  const handleSyncInvoice = async (group, specificInvoice) => {
    const invoice = specificInvoice || group.activeInvoice;
    if (!invoice) return;
    const orderNum = group.orderNumber;
    setOrderLoadings(prev => ({ ...prev, [orderNum]: "Sincronizando..." }));
    try {
      const cnpjEmissor = (
        invoice.cnpj_emissor || 
        invoice.cnpj || 
        invoice.full_data?.cnpj_emissor || 
        invoice.full_data?.cnpj || 
        invoice.focus_payload?.cnpj_emissor || 
        invoice.full_data?.focus_payload?.cnpj_emissor || 
        ""
      ).replace(/\D/g, "");
      const config = fiscalConfigs.find(
        (c) => c.cnpj.replace(/\D/g, "") === cnpjEmissor
      ) || fiscalConfigs[0];

      if (!config) {
        throw new Error("Configuração fiscal não localizada para o CNPJ desta nota.");
      }

      const nfeAmbiente = invoice.ambiente || invoice.full_data?.ambiente || config.ambiente || "homologacao";
      const token = (nfeAmbiente === "producao" ? config.token_producao : config.token_homologacao || "").trim();
      const focusRef = invoice.focus_ref || invoice.id;

      const payload = {
        id: invoice.id,
        ref: focusRef,
        ambiente: nfeAmbiente,
        token: token,
        pb_token: pb.authStore.token
      };
      
      const result = await fiscalService.syncInvoice(payload);
      
      if (result && result.id) {
        setRawData((prev) => 
          prev.map((item) => {
            const invoices = item.expand?.fiscal_invoices_via_order || [];
            const hasThisInvoice = invoices.some(inv => inv.id === result.id);
            if (hasThisInvoice) {
              const updatedInvoices = invoices.map(inv => 
                inv.id === result.id ? { ...inv, ...result } : inv
              );
              return { ...item, expand: { ...item.expand, fiscal_invoices_via_order: updatedInvoices } };
            }
            return item;
          })
        );
      }
      
      alert("Status da nota sincronizado com sucesso!");
    } catch (err) {
      console.error(`Erro ao sincronizar nota ${invoice.id}:`, err);
      
      const currentStatus = invoice.nfe_status || invoice.full_data?.nfe_status;
      if (currentStatus === "processando") {
        try {
          const updatedRecord = await pb.collection("fiscal_invoices").update(invoice.id, {
            nfe_status: "erro",
            nfe_error_message: `Erro na consulta da Focus NFe: ${err.message || "A nota pode não ter sido emitida com sucesso."}`
          });
          
          setRawData((prev) => 
            prev.map((item) => {
              const invoices = item.expand?.fiscal_invoices_via_order || [];
              const hasThisInvoice = invoices.some(inv => inv.id === updatedRecord.id);
              if (hasThisInvoice) {
                const updatedInvoices = invoices.map(inv => 
                  inv.id === updatedRecord.id ? updatedRecord : inv
                );
                return { ...item, expand: { ...item.expand, fiscal_invoices_via_order: updatedInvoices } };
              }
              return item;
            })
          );
        } catch (updateErr) {
          console.error("Erro ao atualizar status para erro após sync falhar na postagem:", updateErr);
        }
      }
      alert("Erro ao consultar SEFAZ: " + err.message);
    } finally {
      setOrderLoadings(prev => {
        const copy = { ...prev };
        delete copy[orderNum];
        return copy;
      });
    }
  };

  const handleCopyTracking = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopiedTracking(code);
    setTimeout(() => setCopiedTracking(""), 2000);
  };



  const handlePrintDanfeLabel = async (groupOrOrder, specificInvoice) => {
    const invoice = specificInvoice || groupOrOrder.activeInvoice || (groupOrOrder.items?.[0]?.expand?.fiscal_invoices_via_order || []).sort((a, b) => new Date(b.created) - new Date(a.created))[0];
    if (!invoice) {
      alert("Nenhuma nota fiscal encontrada.");
      return;
    }
    
    try {
      const isEntrada = invoice.tipo_nota === "garantia_entrada" || 
                        invoice.tipo_nota === "devolucao" || 
                        invoice.focus_payload?.tipo_documento === 0 || 
                        invoice.full_data?.focus_payload?.tipo_documento === 0 || 
                        false;
      const cnpjEmissor = invoice.cnpj_emissor || invoice.full_data?.cnpj_emissor || "";
      const configs = await pb.collection("fiscal_configs").getFullList();
      const config = configs.find(
        (c) => c.cnpj.replace(/\D/g, "") === cnpjEmissor.replace(/\D/g, "")
      ) || configs[0] || {};
      
      const firstItem = groupOrOrder.items?.[0] || {};
      const shippingAddress = firstItem.shipping?.modified_address || firstItem.shipping?.address || {};
      const customer = firstItem.order?.customer || {};
      
      const formatKey = (k) => {
        return k ? k.replace(/(\d{4})/g, "$1 ").trim() : "";
      };

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
        <head>
          <title>DANFE Simplificado #${invoice.nfe_numero}</title>
          <style>
            @page {
              size: 100mm 150mm;
              margin: 0;
            }
            body {
              width: 100mm;
              height: 150mm;
              margin: 0;
              padding: 2mm;
              box-sizing: border-box;
              font-family: Arial, sans-serif;
              font-size: 10px;
              color: #000;
            }
            .container {
              border: 1px solid #000;
              height: 135mm;
              padding: 2mm;
              display: flex;
              flex-direction: column;
              box-sizing: border-box;
            }
            .header {
              display: flex;
              border-bottom: 1px solid #000;
              padding-bottom: 1.5mm;
              margin-bottom: 1.5mm;
            }
            .logo {
              width: 25%;
              font-size: 9px;
              font-weight: bold;
              text-align: center;
              border-right: 1px solid #000;
              padding-right: 1mm;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .emitter {
              width: 75%;
              padding-left: 2mm;
              font-size: 7.5px;
              line-height: 1.2;
            }
            .title-box {
              text-align: center;
              border-bottom: 1px solid #000;
              font-weight: bold;
              font-size: 11px;
              padding-bottom: 1mm;
              margin-bottom: 1.5mm;
            }
            .barcode-container {
              text-align: center;
              margin-bottom: 1.5mm;
            }
            .barcode-img {
              max-width: 95%;
              height: 12mm;
            }
            .key-text {
              font-size: 7.5px;
              text-align: center;
              font-family: monospace;
              margin-top: 1mm;
            }
            .nfe-details {
              display: flex;
              justify-content: space-between;
              border-bottom: 1px solid #000;
              border-top: 1px solid #000;
              padding: 1mm 0;
              margin-bottom: 1.5mm;
              font-weight: bold;
              font-size: 9px;
            }
            .section-title {
              font-weight: bold;
              font-size: 8.5px;
              text-transform: uppercase;
              background: #f0f0f0;
              padding: 0.5mm 1mm;
              margin-bottom: 1mm;
              border: 0.5px solid #000;
            }
            .destinatario {
              flex: 1;
              font-size: 8.5px;
              line-height: 1.3;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="title-box">DANFE SIMPLIFICADO - ${isEntrada ? "ENTRADA" : "SAÍDA"}</div>
            
            <div class="header">
              <div class="logo">SEMPRE<br>JOIAS</div>
              <div class="emitter">
                <strong>${config.razao_social || "SEMPRE JOIAS LTDA"}</strong><br>
                CNPJ: ${config.cnpj || "46.223.109/0001-07"}<br>
                ${config.endereco || "AV WALDEMAR CARLOS PEREIRA, 179 - SALA 6"}<br>
                ${config.bairro || "VILA MATILDE"} - ${config.cidade || "SAO PAULO"} - ${config.uf || "SP"} - CEP: ${config.cep || "03533-001"}
              </div>
            </div>

            <div class="barcode-container">
              <img class="barcode-img" src="https://bwipjs-api.metafloor.com/?bcid=code128&text=${invoice.nfe_chave}&scale=3" /><br>
              <div class="key-text">${formatKey(invoice.nfe_chave)}</div>
            </div>

            <div class="nfe-details">
              <span>Nº: ${invoice.nfe_numero}</span>
              <span>SÉRIE: ${invoice.nfe_serie}</span>
              <span>EMISSÃO: ${new Date(invoice.nfe_data_emissao || invoice.created).toLocaleDateString("pt-BR")}</span>
            </div>

            <div class="section-title">${isEntrada ? "REMETENTE" : "DESTINATÁRIO"}</div>
            <div class="destinatario">
              <strong>${shippingAddress.recipient || customer.name || groupOrOrder.customerName}</strong><br>
              ${shippingAddress.street || ""}, ${shippingAddress.number || "SN"} ${shippingAddress.complemento ? '- ' + shippingAddress.complemento : ''}<br>
              ${shippingAddress.district || ""} - ${shippingAddress.city || ""}/${(shippingAddress.statecode || shippingAddress.state || "").toUpperCase()}<br>
              CEP: ${shippingAddress.zip || ""}
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err) {
      alert("Erro ao gerar etiqueta de DANFE Simplificado: " + err.message);
    }
  };

  const commonCount = processedOrders.filter(o => !String(o.orderNumber || "").toLowerCase().includes("repostagem")).length;
  const repostagemCount = processedOrders.filter(o => String(o.orderNumber || "").toLowerCase().includes("repostagem")).length;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full bg-slate-100 overflow-hidden font-sans">
      {/* MODAL DE EMISSÃO FISCAL */}
      {emittingOrderGroup && (
        <InvoiceGenerationModal
          isOpen={!!emittingOrderGroup}
          orderGroup={emittingOrderGroup}
          invoice={editingInvoice}
          findCatalogItem={findCatalogItem}
          forcedTipoNota={forcedInvoiceTipoNota}
          onClose={() => {
            setEmittingOrderGroup(null);
            setForcedInvoiceTipoNota(null);
            setEditingInvoice(null);
          }}
          onSuccess={() => {
            setEmittingOrderGroup(null);
            setForcedInvoiceTipoNota(null);
            setEditingInvoice(null);
          }}
        />
      )}

      {/* MODAL DE LOGÍSTICA REVERSA */}
      <RequestReverseLogisticsModal
        isOpen={isReversaModalOpen}
        onClose={() => setIsReversaModalOpen(false)}
        onSuccess={loadReverseRequests}
      />

      {/* CABEÇALHO */}
      <header className="bg-white border-b border-slate-200 p-4 shrink-0 flex flex-col md:flex-row justify-between items-center gap-4 shadow-sm z-10">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 text-white p-2.5 rounded-xl shadow-md">
            <Mail size={22} />
          </div>
          <div>
            <h2 className="font-extrabold text-slate-800 text-base leading-tight">Expedição de Pedidos</h2>
            <p className="text-slate-500 text-xs mt-0.5">Gerencie NF-e, Etiquetas de Postagem e Certificados antes de enviar os pedidos para postagem.</p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0">
          <button
            onClick={() => setActiveSubTab("postagem")}
            className={`px-4 py-1.5 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
              activeSubTab === "postagem" 
                ? "bg-white text-slate-800 shadow-sm" 
                : "text-slate-500 hover:text-slate-805"
            }`}
          >
            Envio Ativo (Pedidos)
          </button>
          <button
            onClick={() => setActiveSubTab("reversa")}
            className={`px-4 py-1.5 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
              activeSubTab === "reversa" 
                ? "bg-white text-slate-800 shadow-sm" 
                : "text-slate-500 hover:text-slate-805"
            }`}
          >
            Logística Reversa
          </button>
          <button
            onClick={() => setActiveSubTab("monitoramento")}
            className={`px-4 py-1.5 rounded-lg text-xs font-extrabold transition-all cursor-pointer ${
              activeSubTab === "monitoramento" 
                ? "bg-white text-slate-800 shadow-sm" 
                : "text-slate-500 hover:text-slate-805"
            }`}
          >
            Monitoramento de Envios
          </button>
        </div>

        <div className="relative w-full md:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={
              activeSubTab === "postagem" 
                ? "Buscar por pedido, cliente ou SKU..." 
                : activeSubTab === "monitoramento"
                  ? "Buscar por pedido ou nome do cliente..."
                  : "Buscar por pedido ou cliente..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-lg w-full outline-none focus:border-blue-500 bg-slate-50 focus:bg-white transition-all shadow-inner"
          />
        </div>
      </header>

      {activeSubTab === "postagem" ? (
        <>
          {/* Sub-abas de postagem: Comuns vs Repostagens */}
          <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-2 shrink-0">
            <button
              onClick={() => setActiveQueueType("venda")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                activeQueueType === "venda"
                  ? "bg-slate-100 text-slate-800 border border-slate-300"
                  : "text-slate-500 hover:text-slate-800 border border-transparent"
              }`}
            >
              <span>Pedidos Comuns</span>
              <span className="bg-slate-200 px-1.5 py-0.5 rounded-full text-[10px] font-black">{commonCount}</span>
            </button>
            <button
              onClick={() => setActiveQueueType("repostagem")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                activeQueueType === "repostagem"
                  ? "bg-amber-50 text-amber-800 border border-amber-300"
                  : "text-slate-500 hover:text-amber-750 border border-transparent"
              }`}
            >
              <span>Repostagens & Garantias</span>
              <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full text-[10px] font-black">{repostagemCount}</span>
            </button>
          </div>

          {/* CARDS DE RESUMO / FILTROS CLICÁVEIS */}
          <section className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 shrink-0 bg-slate-100">
            
            {/* Total no Fluxo */}
            <button 
              onClick={() => setActiveFilter("all")}
              className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                activeFilter === "all" 
                  ? "bg-blue-600 text-white border-blue-700 ring-2 ring-blue-400/30" 
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className={`p-2.5 rounded-lg ${activeFilter === "all" ? "bg-white/15 text-white" : "bg-blue-50 text-blue-600"}`}>
                <Mail size={18} />
              </div>
              <div>
                <div className={`text-[10px] uppercase font-bold ${activeFilter === "all" ? "text-blue-100" : "text-slate-400"}`}>Total no Fluxo</div>
                <div className="text-xl font-extrabold">{stats.total}</div>
              </div>
            </button>

            {/* Pendente NF-e */}
            <button 
              onClick={() => toggleFilter("nfe")}
              className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                activeFilter === "nfe" 
                  ? "bg-amber-500 text-white border-amber-600 ring-2 ring-amber-400/30" 
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className={`p-2.5 rounded-lg ${activeFilter === "nfe" ? "bg-white/15 text-white" : "bg-amber-50 text-amber-605"}`}>
                <FileText size={18} />
              </div>
              <div>
                <div className={`text-[10px] uppercase font-bold ${activeFilter === "nfe" ? "text-amber-100" : "text-slate-400"}`}>Pendente NF-e</div>
                <div className="text-xl font-extrabold">{stats.pendingNfe}</div>
              </div>
            </button>

            {/* Pendente Etiqueta */}
            <button 
              onClick={() => toggleFilter("label")}
              className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                activeFilter === "label" 
                  ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-400/30" 
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className={`p-2.5 rounded-lg ${activeFilter === "label" ? "bg-white/15 text-white" : "bg-indigo-50 text-indigo-600"}`}>
                <Truck size={18} />
              </div>
              <div>
                <div className={`text-[10px] uppercase font-bold ${activeFilter === "label" ? "text-indigo-100" : "text-slate-400"}`}>Pendente Etiqueta</div>
                <div className="text-xl font-extrabold">{stats.pendingLabel}</div>
              </div>
            </button>

            {/* Pendente Certificado */}
            <button 
              onClick={() => toggleFilter("cert")}
              className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                activeFilter === "cert" 
                  ? "bg-teal-600 text-white border-teal-700 ring-2 ring-teal-400/30" 
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className={`p-2.5 rounded-lg ${activeFilter === "cert" ? "bg-white/15 text-white" : "bg-teal-50 text-teal-600"}`}>
                <Award size={18} />
              </div>
              <div>
                <div className={`text-[10px] uppercase font-bold ${activeFilter === "cert" ? "text-teal-100" : "text-slate-400"}`}>Pendente Certificado</div>
                <div className="text-xl font-extrabold">{stats.pendingCert}</div>
              </div>
            </button>

          </section>

          {/* LISTAGEM PRINCIPAL */}
          <div className="flex-1 p-4 pt-0 overflow-hidden flex flex-col">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 flex flex-col overflow-hidden">
              
              {/* BARRA DE AÇÕES EM LOTE */}
              {selectedOrders.length > 0 && (
                <div className="bg-indigo-50 border-b border-indigo-100 px-4 py-3 flex items-center justify-between animate-slide-in shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-indigo-800">
                      {selectedOrders.length} {selectedOrders.length === 1 ? "pedido selecionado" : "pedidos selecionados"}
                    </span>
                    <button 
                      onClick={() => setSelectedOrders([])}
                      className="text-[10px] font-bold text-indigo-500 hover:text-indigo-700 underline"
                    >
                      Limpar seleção
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* IMPRIMIR COMBOS SELECIONADOS */}
                    <button
                      onClick={handlePrintComboBatch}
                      disabled={!!batchLoading}
                      className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all cursor-pointer"
                    >
                      {batchLoading === "print" ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Printer size={12} />
                      )}
                      <span>Imprimir Combos ({selectedOrders.length})</span>
                    </button>

                    {/* CONCLUIR SELECIONADOS EM LOTE */}
                    <button
                      onClick={handleFinalizePostageBatch}
                      disabled={!!batchLoading}
                      className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all cursor-pointer"
                    >
                      {batchLoading === "finalize" ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <CheckCircle size={12} />
                      )}
                      <span>Marcar Prontos ({selectedOrders.length})</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tabela */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {displayOrders.length === 0 ? (
                  <div className="h-full flex flex-col justify-center items-center py-16 text-slate-400 bg-slate-50/50">
                    <CheckCircle size={40} className="text-slate-300 mb-2" />
                    <span className="text-sm font-semibold">Nenhum pedido encontrado</span>
                    <span className="text-xs text-slate-400 mt-1">
                      {activeFilter === "all" 
                        ? "Tudo limpo por aqui." 
                        : "Nenhum pedido pendente nesta etapa com as condições do filtro."}
                    </span>
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-200 tracking-wider">
                        <th className="py-3 px-4 w-10 text-center">
                          <input 
                            type="checkbox"
                            checked={displayOrders.length > 0 && selectedOrders.length === displayOrders.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedOrders(displayOrders.map(o => o.orderNumber));
                              } else {
                                setSelectedOrders([]);
                              }
                            }}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4 cursor-pointer"
                          />
                        </th>
                        <th className="py-3 px-4 w-28">Pedido</th>
                        <th className="py-3 px-4 w-60">Cliente</th>
                        <th className="py-3 px-4 w-32">Envio</th>
                        <th className="py-3 px-4 w-56 text-center font-bold">1. Nota Fiscal (NF-e)</th>
                        <th className="py-3 px-4 w-56 text-center font-bold">2. Etiqueta Correios</th>
                        <th className="py-3 px-4 w-52 text-center font-bold">3. Certificado</th>
                        <th className="py-3 px-4 text-right pr-6 w-44">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {displayOrders.map((order) => (
                        <OrderRow
                          key={order.orderNumber}
                          order={order}
                          loading={orderLoadings[order.orderNumber]}
                          onEmit={(forcedType, invToEdit) => {
                            setForcedInvoiceTipoNota(forcedType || null);
                            setEditingInvoice(invToEdit || null);
                            setEmittingOrderGroup(order);
                          }}
                          onSync={(inv) => handleSyncInvoice(order, inv)}
                          onGenerateLabel={() => handleGenerateCorreiosLabel(order)}
                          onPrintCertificate={() => handlePrintCertificates(order)}
                          onFinalize={() => handleFinalizePostage(order)}
                          onCopy={handleCopyTracking}
                          copied={copiedTracking}
                          onPrintDanfe={(inv) => handlePrintDanfeLabel(order, inv)}
                          onRepostLabel={() => handleRepostLabel(order)}
                          onPrintCombo={() => handlePrintComboUnified(order)}
                          selected={selectedOrders.includes(order.orderNumber)}
                          onToggleSelection={() => {
                            setSelectedOrders(prev => 
                              prev.includes(order.orderNumber) 
                                ? prev.filter(num => num !== order.orderNumber) 
                                : [...prev, order.orderNumber]
                            );
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </>
      ) : activeSubTab === "reversa" ? (
        <>
          {/* CARDS DE RESUMO REVERSA */}
          <section className="p-4 flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 shrink-0 bg-slate-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
              {/* Total Reversas */}
              <button 
                onClick={() => setActiveReversaFilter("all")}
                className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                  activeReversaFilter === "all" 
                    ? "bg-blue-600 text-white border-blue-700 ring-2 ring-blue-400/30" 
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className={`p-2.5 rounded-lg ${activeReversaFilter === "all" ? "bg-white/15 text-white" : "bg-blue-50 text-blue-600"}`}>
                  <RefreshCw size={18} />
                </div>
                <div>
                  <div className={`text-[10px] uppercase font-bold ${activeReversaFilter === "all" ? "text-blue-100" : "text-slate-400"}`}>Total Reversas</div>
                  <div className="text-xl font-extrabold">{reverseStats.total}</div>
                </div>
              </button>

              {/* Gerados */}
              <button 
                onClick={() => setActiveReversaFilter("GERADO")}
                className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                  activeReversaFilter === "GERADO" 
                    ? "bg-amber-500 text-white border-amber-600 ring-2 ring-amber-400/30" 
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className={`p-2.5 rounded-lg ${activeReversaFilter === "GERADO" ? "bg-white/15 text-white" : "bg-amber-50 text-amber-600"}`}>
                  <Info size={18} />
                </div>
                <div>
                  <div className={`text-[10px] uppercase font-bold ${activeReversaFilter === "GERADO" ? "text-amber-100" : "text-slate-400"}`}>Aguardando Postagem</div>
                  <div className="text-xl font-extrabold">{reverseStats.pending}</div>
                </div>
              </button>

              {/* Postados */}
              <button 
                onClick={() => setActiveReversaFilter("POSTADO")}
                className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                  activeReversaFilter === "POSTADO" 
                    ? "bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-400/30" 
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className={`p-2.5 rounded-lg ${activeReversaFilter === "POSTADO" ? "bg-white/15 text-white" : "bg-indigo-50 text-indigo-600"}`}>
                  <Truck size={18} />
                </div>
                <div>
                  <div className={`text-[10px] uppercase font-bold ${activeReversaFilter === "POSTADO" ? "text-indigo-100" : "text-slate-400"}`}>Em Trânsito</div>
                  <div className="text-xl font-extrabold">{reverseStats.inTransit}</div>
                </div>
              </button>

              {/* Entregues */}
              <button 
                onClick={() => setActiveReversaFilter("ENTREGUE")}
                className={`p-4 rounded-xl border flex items-center gap-3 text-left transition-all w-full shadow-sm hover:shadow focus:outline-none ${
                  activeReversaFilter === "ENTREGUE" 
                    ? "bg-teal-600 text-white border-teal-700 ring-2 ring-teal-400/30" 
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className={`p-2.5 rounded-lg ${activeReversaFilter === "ENTREGUE" ? "bg-white/15 text-white" : "bg-teal-50 text-teal-600"}`}>
                  <CheckCircle size={18} />
                </div>
                <div>
                  <div className={`text-[10px] uppercase font-bold ${activeReversaFilter === "ENTREGUE" ? "text-teal-100" : "text-slate-400"}`}>Entregues</div>
                  <div className="text-xl font-extrabold">{reverseStats.delivered}</div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setIsReversaModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs px-5 py-4 rounded-xl shadow-md shadow-emerald-600/10 cursor-pointer transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-1.5 shrink-0"
            >
              <Plus size={14} /> Solicitar Reversa
            </button>
          </section>

          {/* LISTAGEM REVERSA */}
          <div className="flex-1 p-4 pt-0 overflow-hidden flex flex-col">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {loadingReversa ? (
                  <div className="h-full flex flex-col justify-center items-center py-16 text-slate-450 gap-3">
                    <RefreshCw className="animate-spin text-blue-500" size={32} />
                    <span className="text-sm font-bold">Carregando logística reversa...</span>
                  </div>
                ) : filteredReversaRequests.length === 0 ? (
                  <div className="h-full flex flex-col justify-center items-center py-16 text-slate-400 bg-slate-50/50">
                    <CheckCircle size={40} className="text-slate-300 mb-2" />
                    <span className="text-sm font-semibold">Nenhuma logística reversa encontrada</span>
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-200 tracking-wider">
                        <th className="py-3 px-4 w-28">Pedido</th>
                        <th className="py-3 px-4">Cliente</th>
                        <th className="py-3 px-4 w-48 text-center font-bold">Autorização de Postagem</th>
                        <th className="py-3 px-4 w-44 text-center font-bold">Código de Rastreio</th>
                        <th className="py-3 px-4 w-36 text-center font-bold">Validade</th>
                        <th className="py-3 px-4 w-28 text-center font-bold">Status</th>
                        <th className="py-3 px-4 text-right pr-6 w-32">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {filteredReversaRequests.map((req) => (
                        <tr key={req.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-3 px-4 font-mono font-extrabold text-blue-600">
                            #{req.order_number}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-slate-750">{req.customer_name}</span>
                              <span className="text-[10px] text-slate-400 font-mono">CPF: {req.customer_cpf}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <span className="font-mono text-xs font-black text-slate-800">{req.codigo_autorizacao}</span>
                              <button 
                                onClick={() => handleCopyTracking(req.codigo_autorizacao)}
                                className="text-slate-400 hover:text-slate-700 p-0.5 rounded hover:bg-slate-100 cursor-pointer" 
                                title="Copiar código de autorização"
                              >
                                {copiedTracking === req.codigo_autorizacao ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {req.codigo_rastreio ? (
                              <div className="flex items-center justify-center gap-1.5">
                                <span className="font-mono text-xs font-bold text-slate-700">{req.codigo_rastreio}</span>
                                <button 
                                  onClick={() => handleCopyTracking(req.codigo_rastreio)}
                                  className="text-slate-400 hover:text-slate-700 p-0.5 rounded hover:bg-slate-100 cursor-pointer" 
                                  title="Copiar código de rastreamento"
                                >
                                  {copiedTracking === req.codigo_rastreio ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                                </button>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs italic">Aguardando postagem</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center font-bold text-slate-600">
                            {req.validade ? new Date(req.validade).toLocaleDateString("pt-BR") : "---"}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase border tracking-wider ${
                              req.status === "ENTREGUE" 
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100" 
                                : req.status === "POSTADO"
                                  ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                                  : req.status === "EXPIRADO"
                                    ? "bg-rose-50 text-rose-700 border-rose-100"
                                    : "bg-amber-50 text-amber-700 border-amber-100"
                            }`}>
                              {req.status === "GERADO" ? "Pendente" : req.status === "POSTADO" ? "Postado" : req.status === "ENTREGUE" ? "Entregue" : "Expirado"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right pr-6">
                            <div className="flex items-center justify-end gap-2">
                              {orderLoadings[req.id] ? (
                                <span className="text-xs text-slate-400 font-bold animate-pulse">{orderLoadings[req.id]}</span>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleSyncReversa(req)}
                                    className="p-1.5 text-slate-450 hover:text-blue-600 hover:bg-blue-50 border border-slate-205 rounded-lg transition-colors cursor-pointer bg-white"
                                    title="Sincronizar Status da Reversa"
                                  >
                                    <RefreshCw size={12} />
                                  </button>
                                  {(req.status === "GERADO" || req.status === "EXPIRADO") && (
                                    <button
                                      type="button"
                                      onClick={() => handleRevalidateReversa(req)}
                                      className="p-1.5 text-slate-450 hover:text-emerald-600 hover:bg-emerald-50 border border-slate-205 rounded-lg transition-colors cursor-pointer bg-white"
                                      title="Estender Prazo de Postagem (+Dias)"
                                    >
                                      <Plus size={12} />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteReversa(req.id)}
                                    className="p-1.5 text-slate-450 hover:text-red-650 hover:bg-red-50 border border-slate-255 rounded-lg transition-colors cursor-pointer bg-white"
                                    title="Deletar Solicitação"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <MonitoramentoSubTab
          orders={processedOrders}
          setRawData={setRawData}
          searchTerm={searchTerm}
          filterStartDate={filterStartDate}
          setFilterStartDate={setFilterStartDate}
          filterEndDate={filterEndDate}
          setFilterEndDate={setFilterEndDate}
          selectedDetailsOrder={selectedDetailsOrder}
          setSelectedDetailsOrder={setSelectedDetailsOrder}
          syncAllLoading={syncAllLoading}
          setSyncAllLoading={setSyncAllLoading}
          orderLoadings={orderLoadings}
          setOrderLoadings={setOrderLoadings}
          copiedTracking={copiedTracking}
          setCopiedTracking={setCopiedTracking}
          handleCopyTracking={handleCopyTracking}
          postageLabelsService={postageLabelsService}
          correiosService={correiosService}
          pb={pb}
        />
      )}

      {/* Seletor de serviço para frete grátis/customizado */}
      {freeShippingOrder && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-w-sm w-full mx-4 animate-scale-in">
            <h3 className="font-extrabold text-slate-800 text-lg mb-2">Serviço de Envio</h3>
            <p className="text-slate-500 text-xs mb-4">
              O pedido <strong>#{freeShippingOrder.orderNumber}</strong> possui o frete <strong>{freeShippingOrder.shippingMethod || "FREE"}</strong>. Selecione qual serviço de postagem deseja utilizar para este pedido:
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  const group = freeShippingOrder;
                  setFreeShippingOrder(null);
                  generateLabelWithService(group, "03298"); // PAC
                }}
                className="flex items-center justify-between p-3.5 border border-blue-200 bg-blue-50/50 hover:bg-blue-50 text-blue-700 font-extrabold rounded-xl transition-all cursor-pointer hover:scale-[1.02]"
              >
                <div className="flex items-center gap-2">
                  <span className="bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded">PAC</span>
                  <span className="text-xs">Postagem Econômica</span>
                </div>
                <ArrowRight size={14} />
              </button>

              <button
                onClick={() => {
                  const group = freeShippingOrder;
                  setFreeShippingOrder(null);
                  generateLabelWithService(group, "03220"); // SEDEX
                }}
                className="flex items-center justify-between p-3.5 border border-red-200 bg-red-50/50 hover:bg-red-50 text-red-700 font-extrabold rounded-xl transition-all cursor-pointer hover:scale-[1.02]"
              >
                <div className="flex items-center gap-2">
                  <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded">SEDEX</span>
                  <span className="text-xs">Postagem Expressa</span>
                </div>
                <ArrowRight size={14} />
              </button>

              <button
                onClick={() => {
                  const group = freeShippingOrder;
                  setFreeShippingOrder(null);
                  generateLabelWithService(group, "03140"); // SEDEX 12
                }}
                className="flex items-center justify-between p-3.5 border border-purple-200 bg-purple-50/50 hover:bg-purple-50 text-purple-700 font-extrabold rounded-xl transition-all cursor-pointer hover:scale-[1.02]"
              >
                <div className="flex items-center gap-2">
                  <span className="bg-purple-600 text-white text-[10px] font-black px-2 py-0.5 rounded">SEDEX 12</span>
                  <span className="text-xs">Entrega até as 12h do dia seguinte</span>
                </div>
                <ArrowRight size={14} />
              </button>

              <button
                onClick={async () => {
                  const group = freeShippingOrder;
                  setFreeShippingOrder(null);
                  await handleSelectMotoboy(group);
                }}
                className="flex items-center justify-between p-3.5 border border-amber-200 bg-amber-50/50 hover:bg-amber-50 text-amber-700 font-extrabold rounded-xl transition-all cursor-pointer hover:scale-[1.02]"
              >
                <div className="flex items-center gap-2">
                  <span className="bg-amber-600 text-white text-[10px] font-black px-2 py-0.5 rounded">MOTOBOY</span>
                  <span className="text-xs">Entrega Local</span>
                </div>
                <ArrowRight size={14} />
              </button>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setFreeShippingOrder(null)}
                className="text-xs text-slate-500 hover:text-slate-700 font-semibold px-3 py-1.5 rounded-lg"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação e Edição de Endereço/Serviço */}
      {confirmLabelGroup && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-w-lg w-full animate-scale-in text-left">
            <h3 className="font-extrabold text-slate-800 text-base mb-1">Confirmar Detalhes da Postagem</h3>
            <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-4">
              Pedido #{confirmLabelGroup.orderNumber} • {confirmLabelGroup.customerName}
            </p>
            
            <div className="space-y-4 text-xs font-semibold text-slate-700">
              {/* Modalidade de Envio */}
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Serviço de Postagem (Correios)</label>
                <select
                  value={confirmServiceCode}
                  onChange={(e) => setConfirmServiceCode(e.target.value)}
                  className="w-full p-2.5 border border-slate-200 bg-slate-50 hover:bg-slate-100/30 rounded-xl font-bold text-slate-800 outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                >
                  <option value="03298">PAC (Contrato 03298)</option>
                  <option value="03220">SEDEX (Contrato 03220)</option>
                  <option value="03158">SEDEX 10 (Contrato 03158)</option>
                  <option value="03140">SEDEX 12 (Contrato 03140)</option>
                  <option value="MOTOBOY">MOTOBOY (Entrega Local)</option>
                </select>
              </div>

              {confirmServiceCode !== "MOTOBOY" && (
                <div className="space-y-3 pt-3 border-t border-slate-100">
                  <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider">Endereço do Destinatário</span>
                  
                  {/* Destinatário */}
                  <div>
                    <label className="block text-[9px] text-slate-500 mb-0.5">Nome do Destinatário</label>
                    <input
                      type="text"
                      className="w-full p-2 border border-slate-200 bg-slate-50 rounded-lg outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                      value={confirmAddress.recipient}
                      onChange={(e) => setConfirmAddress(prev => ({ ...prev, recipient: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {/* CEP */}
                    <div className="col-span-1">
                      <label className="block text-[9px] text-slate-500 mb-0.5">CEP</label>
                      <input
                        type="text"
                        maxLength="9"
                        className="w-full p-2 border border-slate-200 bg-slate-50 rounded-lg outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                        value={confirmAddress.zip}
                        onChange={(e) => handleZipChange(e.target.value)}
                        required
                      />
                    </div>
                    {/* Logradouro */}
                    <div className="col-span-2">
                      <div className="flex justify-between items-center mb-0.5">
                        <label className="block text-[9px] text-slate-500">Logradouro / Rua</label>
                        {confirmAddress.street && confirmAddress.street.length >= 35 && (
                          <span className={`text-[8px] font-bold uppercase tracking-wider ${confirmAddress.street.length >= 45 ? "text-red-500" : "text-amber-600"}`}>
                            {confirmAddress.street.length}/45
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        maxLength="45"
                        className={`w-full p-2 border rounded-lg outline-none transition-all shadow-inner font-bold ${confirmAddress.street && confirmAddress.street.length >= 45 ? "border-red-300 bg-red-50/10 focus:border-red-500 text-red-900" : "border-slate-200 bg-slate-50 focus:border-amber-500 focus:bg-white"}`}
                        value={confirmAddress.street}
                        onChange={(e) => setConfirmAddress(prev => ({ ...prev, street: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {/* Número */}
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-0.5">Número</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-slate-200 bg-slate-50 rounded-lg outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                        value={confirmAddress.number}
                        onChange={(e) => setConfirmAddress(prev => ({ ...prev, number: e.target.value }))}
                        required
                      />
                    </div>
                    {/* Complemento */}
                    <div className="col-span-2">
                      <div className="flex justify-between items-center mb-0.5">
                        <label className="block text-[9px] text-slate-500">Complemento</label>
                        {confirmAddress.complemento && confirmAddress.complemento.length >= 22 && (
                          <span className={`text-[8px] font-bold uppercase tracking-wider ${confirmAddress.complemento.length >= 30 ? "text-red-500" : "text-amber-600"}`}>
                            {confirmAddress.complemento.length}/30
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        maxLength="30"
                        className={`w-full p-2 border rounded-lg outline-none transition-all shadow-inner font-bold ${confirmAddress.complemento && confirmAddress.complemento.length >= 30 ? "border-red-300 bg-red-50/10 focus:border-red-500 text-red-900" : "border-slate-200 bg-slate-50 focus:border-amber-500 focus:bg-white"}`}
                        value={confirmAddress.complemento}
                        onChange={(e) => setConfirmAddress(prev => ({ ...prev, complemento: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {/* Bairro */}
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-0.5">Bairro</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-slate-200 bg-slate-50 rounded-lg outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                        value={confirmAddress.district}
                        onChange={(e) => setConfirmAddress(prev => ({ ...prev, district: e.target.value }))}
                        required
                      />
                    </div>
                    {/* Cidade */}
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-0.5">Cidade</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-slate-200 bg-slate-50 rounded-lg outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold"
                        value={confirmAddress.city}
                        onChange={(e) => setConfirmAddress(prev => ({ ...prev, city: e.target.value }))}
                        required
                      />
                    </div>
                    {/* UF */}
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-0.5">UF (Estado)</label>
                      <input
                        type="text"
                        maxLength="2"
                        className="w-full p-2 border border-slate-200 bg-slate-50 rounded-lg outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-bold uppercase"
                        value={confirmAddress.state}
                        onChange={(e) => setConfirmAddress(prev => ({ ...prev, state: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  {/* Serviços Adicionais */}
                  {(() => {
                    const activeInvoice = confirmLabelGroup?.activeInvoice || (confirmLabelGroup?.items?.[0]?.expand?.fiscal_invoices_via_order || []).sort((a, b) => new Date(b.created) - new Date(a.created))[0];
                    const hasInvoice = !!activeInvoice?.nfe_chave;
                    return (
                      <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-4">
                        <label className="flex items-center gap-2.5 p-2 bg-slate-50 hover:bg-slate-100/60 border border-slate-200 rounded-xl cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={confirmAr}
                            onChange={(e) => setConfirmAr(e.target.checked)}
                            className="text-blue-600 focus:ring-blue-500 rounded"
                          />
                          <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wide">Aviso de Recebimento (AR)</span>
                        </label>

                        <label className={`flex items-center gap-2.5 p-2 border rounded-xl transition-all ${
                          hasInvoice 
                            ? "bg-slate-50 hover:bg-slate-100/60 border-slate-200 cursor-pointer" 
                            : "bg-slate-100 border-slate-200 cursor-not-allowed opacity-60"
                        }`}>
                          <input
                            type="checkbox"
                            checked={confirmSeguro}
                            disabled={!hasInvoice}
                            onChange={(e) => {
                              setConfirmSeguro(e.target.checked);
                              if (e.target.checked) {
                                setConfirmValorSegurado(confirmLabelGroup?.totalValue || 0);
                              }
                            }}
                            className="text-blue-600 focus:ring-blue-500 rounded disabled:opacity-50"
                          />
                          <div className="flex flex-col">
                            <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wide">Seguro (Valor Declarado)</span>
                            {!hasInvoice && (
                              <span className="text-[8px] text-amber-600 font-bold leading-none mt-0.5">Exige Nota Fiscal emitida</span>
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })()}

                  {confirmSeguro && (
                    <div className="pt-2 animate-fade-in col-span-2">
                      <label className="block text-[9px] text-slate-500 mb-0.5">Valor do Seguro (Declarado) em R$</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full p-2 border border-slate-200 bg-slate-50 rounded-lg outline-none focus:border-amber-500 focus:bg-white transition-all shadow-inner font-mono font-bold"
                        value={confirmValorSegurado}
                        onChange={(e) => setConfirmValorSegurado(parseFloat(e.target.value) || 0)}
                        required
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 pt-3 border-t border-slate-100 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmLabelGroup(null)}
                className="text-xs text-slate-500 hover:text-slate-700 font-bold px-4 py-2 rounded-lg"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  const streetVal = (confirmAddress.street || "").trim();
                  const compVal = (confirmAddress.complemento || "").trim();

                  if (confirmServiceCode !== "MOTOBOY") {
                    if (streetVal.length > 45) {
                      alert(`O Endereço/Rua não pode ultrapassar 45 caracteres para os Correios (atualmente com ${streetVal.length}). Por favor, abrevie.`);
                      return;
                    }
                    if (compVal.length > 30) {
                      alert(`O Complemento do endereço não pode ultrapassar 30 caracteres para os Correios (atualmente com ${compVal.length}). Por favor, abrevie.`);
                      return;
                    }
                  }

                  const group = confirmLabelGroup;
                  setConfirmLabelGroup(null);
                  if (confirmServiceCode === "MOTOBOY") {
                    await handleSelectMotoboy(group);
                  } else {
                    await generateLabelWithService(
                      group, 
                      confirmServiceCode, 
                      confirmAddress, 
                      confirmAr, 
                      confirmSeguro, 
                      confirmValorSegurado
                    );
                  }
                }}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-5 py-2 rounded-lg shadow-md transition-all uppercase active:scale-98"
              >
                Gerar Etiqueta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// COMPONENTE DE LINHA DE PEDIDO NA TABELA
function OrderRow({ 
  order, loading, onEmit, onSync, onGenerateLabel, onPrintCertificate, onFinalize, onCopy, copied, onPrintDanfe, onRepostLabel, onPrintCombo, selected, onToggleSelection 
}) {
  // Helpers de Estilos para Métodos de Envio
  const renderShippingBadge = () => {
    const method = String(order.shippingMethod).trim().toUpperCase();
    if (method.includes("SEDEX")) {
      return <span className="bg-red-50 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded border border-red-200">SEDEX</span>;
    }
    if (method.includes("PAC")) {
      return <span className="bg-yellow-50 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded border border-yellow-200">PAC</span>;
    }
    return <span className="bg-slate-50 text-slate-650 text-[10px] font-bold px-2 py-0.5 rounded border border-slate-200">{method}</span>;
  };

  // Render do Bloco de NF-e
  const renderNfeBlock = () => {
    const isRepostagem = String(order.orderNumber || "").toLowerCase().includes("repostagem");

    if (isRepostagem) {
      const invoices = order.items?.[0]?.expand?.fiscal_invoices_via_order || [];
      const entradaInv = [...invoices].filter(i => i.tipo_nota === "garantia_entrada").sort((a, b) => new Date(b.created) - new Date(a.created))[0];
      const comedyInv = [...invoices].filter(i => i.tipo_nota === "venda").sort((a, b) => new Date(b.created) - new Date(a.created))[0];
      const saidaInv = [...invoices].filter(i => i.tipo_nota === "garantia_saida").sort((a, b) => new Date(b.created) - new Date(a.created))[0];

      const hasEntrada = entradaInv && entradaInv.nfe_status === "autorizada";
      const hasSaida = saidaInv && saidaInv.nfe_status === "autorizada";

      return (
        <div className="flex flex-col gap-1.5 p-1.5 bg-slate-50 border border-slate-200 rounded-lg max-w-[190px] mx-auto text-left leading-normal">
          {/* Etapa 1: Entrada */}
          <div className="flex items-center justify-between gap-1 text-[10px]">
            <span className="font-bold text-slate-500">1. Entrada:</span>
            {hasEntrada ? (
              <span className="text-emerald-700 font-extrabold flex items-center gap-0.5" title={`NF #${entradaInv.nfe_numero}`}>
                <CheckCircle size={10} className="text-emerald-600 shrink-0" /> #{(entradaInv.nfe_numero || "").substring(0, 5)}
              </span>
            ) : entradaInv?.nfe_status === "processando" ? (
              <button
                onClick={() => onSync(entradaInv)}
                className="px-1 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-[9px] font-extrabold rounded flex items-center gap-0.5 animate-pulse cursor-pointer"
              >
                <Loader2 size={8} className="animate-spin shrink-0" /> Sinc.
              </button>
            ) : entradaInv?.nfe_status === "erro" || entradaInv?.nfe_status === "erro_autorizacao" ? (
              <button
                onClick={() => onEmit("garantia_entrada", entradaInv)}
                className="px-1 py-0.5 bg-red-50 hover:bg-red-100 text-red-600 text-[9px] font-extrabold rounded flex items-center gap-0.5 cursor-pointer"
                title={entradaInv.nfe_error_message}
              >
                <ShieldAlert size={8} className="shrink-0" /> Erro
              </button>
            ) : (
              <button
                onClick={() => onEmit("garantia_entrada")}
                className="px-1.5 py-0.5 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-extrabold rounded cursor-pointer transition-all"
              >
                Emitir
              </button>
            )}
          </div>

          {/* Etapa 2: Saída */}
          <div className="flex items-center justify-between gap-1 text-[10px] pt-1.5 border-t border-slate-200">
            <span className="font-bold text-slate-500">2. Saída:</span>
            {hasSaida ? (
              <span className="text-emerald-700 font-extrabold flex items-center gap-0.5" title={`NF #${saidaInv?.nfe_numero}`}>
                <CheckCircle size={10} className="text-emerald-600 shrink-0" /> #{(saidaInv?.nfe_numero || "").substring(0, 5)}
              </span>
            ) : saidaInv?.nfe_status === "processando" ? (
              <button
                onClick={() => onSync(saidaInv)}
                className="px-1 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-[9px] font-extrabold rounded flex items-center gap-0.5 animate-pulse cursor-pointer"
              >
                <Loader2 size={8} className="animate-spin shrink-0" /> Sinc.
              </button>
            ) : saidaInv?.nfe_status === "erro" || saidaInv?.nfe_status === "erro_autorizacao" ? (
              <button
                onClick={() => onEmit("garantia_saida", saidaInv)}
                className="px-1 py-0.5 bg-red-50 hover:bg-red-100 text-red-600 text-[9px] font-extrabold rounded flex items-center gap-0.5 cursor-pointer"
                title={saidaInv?.nfe_error_message}
              >
                <ShieldAlert size={8} className="shrink-0" /> Erro
              </button>
            ) : (
              <button
                onClick={() => onEmit("garantia_saida")}
                disabled={!hasEntrada}
                className="px-1.5 py-0.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-350 disabled:cursor-not-allowed text-white text-[9px] font-extrabold rounded cursor-pointer transition-all"
                title={!hasEntrada ? "Emita a nota de entrada primeiro" : ""}
              >
                Emitir
              </button>
            )}
          </div>
          
          {/* Links de Impressão */}
          {(hasEntrada || hasSaida) && (
            <div className="flex justify-around gap-2 text-[8px] font-black uppercase text-blue-600 tracking-wider pt-1 border-t border-slate-200">
              {hasEntrada && (
                <button onClick={() => onPrintDanfe(entradaInv)} className="hover:underline cursor-pointer">
                  DANFE Ent.
                </button>
              )}
              {hasSaida && (
                <button onClick={() => onPrintDanfe(saidaInv)} className="hover:underline cursor-pointer">
                  DANFE Saí.
                </button>
              )}
            </div>
          )}
        </div>
      );
    }

    const status = order.nfeStatus;

    if (status === "autorizada") {
      const nfeNum = order.activeInvoice?.nfe_numero || "";
      return (
        <div className="flex flex-col items-center gap-1">
          <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-100 flex items-center gap-1">
            <CheckCircle size={10} /> Autorizada
          </span>
          <span className="text-[10px] text-slate-450 font-bold font-mono">NF #{nfeNum}</span>
          <button
            onClick={() => onPrintDanfe()}
            className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-bold mt-1"
            title="Imprimir DANFE Simplificado (Etiqueta 10x15)"
          >
            <Printer size={9} /> Imprimir DANFE
          </button>
        </div>
      );
    }
    
    if (status === "processando") {
      return (
        <button
          onClick={() => onSync()}
          className="mx-auto flex items-center gap-1.5 px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 text-[11px] font-bold rounded-lg border border-blue-200 transition-colors animate-pulse"
          title="Clique para sincronizar status com a SEFAZ"
        >
          <Loader2 size={12} className="animate-spin" />
          <span>Sincronizar</span>
        </button>
      );
    }

    if (status === "erro" || status === "erro_autorizacao") {
      const errorMsg = order.activeInvoice?.nfe_error_message || "Erro na emissão";
      return (
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={() => onEmit(null, order.activeInvoice)}
            className="mx-auto flex items-center gap-1 px-3 py-1 bg-red-50 hover:bg-red-100 text-red-700 text-[11px] font-bold rounded-lg border border-red-200 transition-colors cursor-pointer"
            title="Corrigir erros e reemitir"
          >
            <ShieldAlert size={12} className="shrink-0 animate-bounce" />
            <span>Corrigir NF</span>
          </button>
          <span className="text-[9px] text-red-500 max-w-[170px] truncate" title={errorMsg}>
            {errorMsg}
          </span>
        </div>
      );
    }

    return (
      <button
        onClick={() => onEmit()}
        className="mx-auto flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold rounded-lg shadow-sm transition-colors cursor-pointer"
      >
        <FileText size={11} />
        <span>Emitir NF-e</span>
      </button>
    );
  };

  // Render do Bloco de Etiqueta Correios
  const renderLabelBlock = () => {
    if (order.hasCorreiosLabel) {
      return (
        <div className="flex flex-col items-center gap-1.5">
          <div className="flex items-center gap-1">
            <span className="font-mono text-xs font-bold text-slate-700">{order.trackingCode}</span>
            <button 
              onClick={() => onCopy(order.trackingCode)}
              className="text-slate-400 hover:text-slate-700 p-0.5 rounded hover:bg-slate-100" 
              title="Copiar código de rastreamento"
            >
              {copied === order.trackingCode ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onGenerateLabel}
              className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-bold cursor-pointer"
            >
              <RefreshCw size={9} /> Reimprimir
            </button>
            <span className="text-slate-300 text-[10px] select-none">|</span>
            <button
              onClick={onRepostLabel}
              className="flex items-center gap-1 text-[10px] text-red-600 hover:text-red-800 font-bold cursor-pointer"
              title="Descartar etiqueta anterior e gerar uma nova"
            >
              Gerar Nova
            </button>
          </div>
        </div>
      );
    }

    // Exige NF-e autorizada antes de liberar a geração da etiqueta (regra CONFAZ ICMS / sem DC-e)
    const canGenerateLabel = !!order.nfeOk && !!order.activeInvoice?.nfe_chave;
    return (
      <button
        onClick={onGenerateLabel}
        disabled={!canGenerateLabel}
        className={`mx-auto flex items-center gap-1 px-3 py-1 text-[11px] font-bold rounded-lg border transition-colors ${
          canGenerateLabel
            ? "bg-blue-600 hover:bg-blue-700 text-white border-transparent shadow-sm cursor-pointer"
            : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
        }`}
        title={canGenerateLabel ? "Gerar Etiqueta" : "Emita e autorize a NF-e antes de gerar a etiqueta dos Correios"}
      >
        <Truck size={11} />
        <span>{canGenerateLabel ? "Gerar Etiqueta" : "Aguardando NF-e"}</span>
      </button>
    );
  };

  // Render do Bloco de Certificado
  const renderCertificateBlock = () => {
    if (order.isCertificatePrinted) {
      return (
        <div className="flex flex-col items-center gap-1">
          <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-100 flex items-center gap-1">
            <CheckCircle size={10} /> Impresso
          </span>
          <button
            onClick={onPrintCertificate}
            className="text-[10px] text-blue-600 hover:text-blue-800 font-bold"
          >
            Reimprimir
          </button>
        </div>
      );
    }

    // O certificado de garantia pode ser impresso a qualquer momento
    return (
      <button
        onClick={onPrintCertificate}
        className="mx-auto flex items-center gap-1 px-3 py-1 text-[11px] font-bold rounded-lg border transition-colors bg-slate-800 hover:bg-black text-white border-transparent shadow-sm cursor-pointer"
        title="Imprimir Certificado"
      >
        <Printer size={11} />
        <span>Imprimir Certificado</span>
      </button>
    );
  };

  return (
    <tr className="hover:bg-slate-50/50 transition-colors relative">
      {/* Loading Overlay por linha */}
      {loading && (
        <td className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center text-blue-600">
          <Loader2 size={18} className="animate-spin mr-2" />
          <span className="text-xs font-bold uppercase tracking-wider">{loading}</span>
        </td>
      )}

      {/* Checkbox de Seleção */}
      <td className="py-3 px-4 text-center">
        <input 
          type="checkbox"
          checked={selected}
          onChange={onToggleSelection}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4 cursor-pointer"
        />
      </td>

      {/* Pedido */}
      <td className="py-3 px-4">
        <div className="flex flex-col">
          <span className="text-blue-600 font-black tracking-tight">#{order.orderNumber}</span>
          <span className="text-[10px] text-slate-400">{order.date.toLocaleDateString("pt-BR")}</span>
        </div>
      </td>

      {/* Cliente */}
      <td className="py-3 px-4">
        <div className="flex flex-col max-w-[220px]">
          <span className="font-bold text-slate-750 truncate" title={order.customerName}>
            {order.customerName}
          </span>
          <span className="text-[10px] text-slate-400 truncate">
            {order.items.map(i => i.sku).join(", ")}
          </span>
        </div>
      </td>

      {/* Envio */}
      <td className="py-3 px-4">
        <div className="flex flex-col items-start gap-1">
          {renderShippingBadge()}
          <span className="text-[10px] font-bold text-slate-500">R$ {order.totalValue.toFixed(2)}</span>
        </div>
      </td>

      {/* 1. NF-e */}
      <td className="py-3 px-4 text-center">
        {renderNfeBlock()}
      </td>

      {/* 2. Etiqueta */}
      <td className="py-3 px-4 text-center">
        {renderLabelBlock()}
      </td>

      {/* 3. Certificado */}
      <td className="py-3 px-4 text-center">
        {renderCertificateBlock()}
      </td>

      {/* Ação de Finalização */}
      <td className="py-3 px-4 text-right pr-6">
        <div className="flex justify-end items-center gap-2">
          <button
            onClick={onPrintCombo}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all hover:scale-[1.02] cursor-pointer"
            title="Imprimir DANFE + Etiqueta Correios + Certificados mesclados em um único spooler"
          >
            <Printer size={11} />
            <span>Imprimir Combo</span>
          </button>

          {order.canFinalize ? (
            <button
              onClick={onFinalize}
              className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all hover:scale-[1.02] cursor-pointer"
              title="Marcar pedido como Pronto para Postar e remover deste fluxo"
            >
              <span>Pronto</span>
              <ArrowRight size={11} />
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-slate-400 bg-slate-100 border border-slate-200 text-[10px] font-semibold px-2.5 py-1.5 rounded-md">
              <Info size={11} /> Em andamento
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}
